import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, MapPin, MagnifyingGlass, Plus, Buildings, Clock, BellSimple, Trash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import JobFormDialog, { WORK_MODE_LABELS, EMP_TYPE_LABELS } from "@/components/JobFormDialog";

const CATEGORIES = ["Engineering", "Product", "Design", "Data & AI", "Marketing", "Sales", "Finance", "Operations", "Human Resources", "Education", "Healthcare", "Legal", "Research", "Nonprofit", "Other"];

function timeago(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function salaryLabel(j) {
  if (!j.salary_min && !j.salary_max) return null;
  const cur = j.salary_currency || "";
  const fmt = (n) => n >= 100000 ? `${(n / 100000).toFixed(n % 100000 ? 1 : 0)}L` : n >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`;
  const range = j.salary_min && j.salary_max ? `${fmt(j.salary_min)}–${fmt(j.salary_max)}` : fmt(j.salary_min || j.salary_max);
  return `${cur} ${range}/${j.salary_period || "year"}`;
}

function JobCard({ j }) {
  return (
    <Link to={`/jobs/${j.id}`} data-testid={`job-card-${j.id}`}>
      <Card className="group h-full border-border/60 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-heading text-lg font-medium group-hover:text-primary">{j.title}</h3>
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground"><Buildings size={14} /> {j.company}</p>
          </div>
          {j.status === "closed" && <Badge variant="secondary" className="shrink-0">Closed</Badge>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge variant="outline" className="rounded-full font-normal">{EMP_TYPE_LABELS[j.employment_type] || j.employment_type}</Badge>
          <Badge variant="outline" className="rounded-full font-normal">{WORK_MODE_LABELS[j.work_mode] || j.work_mode}</Badge>
          {j.category && <Badge variant="outline" className="rounded-full font-normal">{j.category}</Badge>}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {j.location && <span className="flex items-center gap-1"><MapPin size={13} /> {j.location}</span>}
          {salaryLabel(j) && <span className="font-medium text-secondary">{salaryLabel(j)}</span>}
          <span className="flex items-center gap-1"><Clock size={13} /> {timeago(j.created_at)}</span>
        </div>
      </Card>
    </Link>
  );
}

function JobAlertsDialog({ open, onOpenChange }) {
  const [alerts, setAlerts] = useState(null);
  const [cat, setCat] = useState("any");
  const [skill, setSkill] = useState("");
  const [mode, setMode] = useState("any");
  const load = () => api.get("/jobs/alerts").then((r) => setAlerts(r.data.alerts)).catch(() => setAlerts([]));
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => {
    try {
      await api.post("/jobs/alerts", { category: cat, skill: skill.trim(), work_mode: mode });
      setSkill(""); load(); toast.success("Alert added — we'll notify you");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add alert"); }
  };
  const del = async (id) => { try { await api.delete(`/jobs/alerts/${id}`); load(); } catch { toast.error("Could not remove"); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-alerts-dialog">
        <DialogHeader><DialogTitle className="font-heading text-lg">Job alerts</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Get a notification the moment a matching job is posted.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1"><Label className="text-xs">Category</Label>
            <Select value={cat} onValueChange={setCat}><SelectTrigger data-testid="alert-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any category</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}><SelectTrigger data-testid="alert-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any mode</SelectItem>{Object.entries(WORK_MODE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs">Skill (optional)</Label>
            <Input value={skill} onChange={(e) => setSkill(e.target.value)} placeholder="e.g. Python" data-testid="alert-skill" /></div>
        </div>
        <Button className="rounded-full" onClick={add} data-testid="alert-add"><BellSimple size={15} className="mr-1" /> Add alert</Button>
        <div className="mt-2 space-y-2" data-testid="alert-list">
          {alerts === null ? <div className="h-10 animate-pulse rounded-lg bg-muted" /> : alerts.length === 0 ? (
            <p className="py-3 text-center text-sm text-muted-foreground">No alerts yet.</p>
          ) : alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-xl border border-border/60 px-3 py-2 text-sm" data-testid={`alert-${a.id}`}>
              <span>{a.category === "any" ? "Any category" : a.category}{a.skill ? ` · ${a.skill}` : ""}{a.work_mode !== "any" ? ` · ${WORK_MODE_LABELS[a.work_mode]}` : ""}</span>
              <button onClick={() => del(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`alert-del-${a.id}`}><Trash size={16} /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Jobs() {
  const { user } = useAuth();
  const [tab, setTab] = useState("browse");
  const [jobs, setJobs] = useState(null);
  const [mine, setMine] = useState(null);
  const [applied, setApplied] = useState(null);
  const [q, setQ] = useState("");
  const [emp, setEmp] = useState("all");
  const [mode, setMode] = useState("all");
  const [cat, setCat] = useState("all");
  const [showPost, setShowPost] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);

  const loadBrowse = useCallback(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (emp !== "all") p.set("employment_type", emp);
    if (mode !== "all") p.set("work_mode", mode);
    if (cat !== "all") p.set("category", cat);
    api.get(`/jobs?${p.toString()}`).then((r) => setJobs(r.data.jobs)).catch(() => setJobs([]));
  }, [q, emp, mode, cat]);

  useEffect(() => { loadBrowse(); }, [loadBrowse]);
  useEffect(() => {
    if (tab === "posted" && mine === null) api.get("/jobs/mine/posted").then((r) => setMine(r.data.jobs)).catch(() => setMine([]));
    if (tab === "applied" && applied === null) api.get("/jobs/mine/applied").then((r) => setApplied(r.data.applications)).catch(() => setApplied([]));
  }, [tab, mine, applied]);

  const onPosted = () => { setMine(null); loadBrowse(); setTab("posted"); };

  return (
    <main className="mx-auto max-w-6xl px-5 py-8" data-testid="jobs-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-medium tracking-tight"><Briefcase weight="duotone" className="text-secondary" /> Alumni Jobs</h1>
          <p className="mt-1 text-sm text-muted-foreground">Opportunities shared by and for the alumni community.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2 rounded-full" onClick={() => setShowAlerts(true)} data-testid="job-alerts-btn"><BellSimple size={16} /> Job alerts</Button>
          <Button className="gap-2 rounded-full" onClick={() => setShowPost(true)} data-testid="post-job-btn"><Plus size={16} weight="bold" /> Post a job</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList data-testid="jobs-tabs">
          <TabsTrigger value="browse" data-testid="tab-browse">Browse</TabsTrigger>
          <TabsTrigger value="posted" data-testid="tab-posted">My postings</TabsTrigger>
          <TabsTrigger value="applied" data-testid="tab-applied">My applications</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-5">
          <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative sm:col-span-2 lg:col-span-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs, skills, companies" className="rounded-full pl-9" data-testid="jobs-search" />
            </div>
            <Select value={emp} onValueChange={setEmp}>
              <SelectTrigger className="rounded-full" data-testid="jobs-filter-emp"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(EMP_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="rounded-full" data-testid="jobs-filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any mode</SelectItem>
                {Object.entries(WORK_MODE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="rounded-full" data-testid="jobs-filter-cat"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {jobs === null ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-muted" />)}</div>
          ) : jobs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-20 text-center">
              <Briefcase size={40} className="mx-auto text-muted-foreground/40" />
              <p className="mt-3 font-medium">No jobs match your search</p>
              <p className="mt-1 text-sm text-muted-foreground">Be the first to share an opportunity with the community.</p>
              <Button className="mt-4 gap-2 rounded-full" onClick={() => setShowPost(true)}><Plus size={16} /> Post a job</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
              {jobs.map((j) => <JobCard key={j.id} j={j} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="posted" className="mt-5">
          {mine === null ? <div className="h-32 animate-pulse rounded-2xl bg-muted" /> : mine.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">You haven't posted any jobs yet.</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="my-jobs-grid">
              {mine.map((j) => (
                <div key={j.id} className="relative">
                  <JobCard j={j} />
                  {j.application_count > 0 && <span className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-primary-foreground">{j.application_count} applicant{j.application_count > 1 ? "s" : ""}</span>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applied" className="mt-5">
          {applied === null ? <div className="h-32 animate-pulse rounded-2xl bg-muted" /> : applied.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">You haven't applied to any jobs yet.</div>
          ) : (
            <div className="space-y-3" data-testid="applied-list">
              {applied.map(({ application, job }) => (
                <Link key={application.id} to={`/jobs/${job.id}`}>
                  <Card className="flex items-center justify-between gap-3 border-border/60 p-4 hover:shadow-md">
                    <div className="min-w-0">
                      <h3 className="truncate font-medium">{job.title}</h3>
                      <p className="truncate text-sm text-muted-foreground">{job.company}</p>
                    </div>
                    <Badge variant={application.status === "shortlisted" ? "default" : "secondary"} className="shrink-0 capitalize">{application.status}</Badge>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <JobFormDialog open={showPost} onOpenChange={setShowPost} onSaved={onPosted} />
      <JobAlertsDialog open={showAlerts} onOpenChange={setShowAlerts} />
    </main>
  );
}
